import { m } from "@plannello/i18n/messages";
import { useSyncExternalStore } from "react";
import "./live.css";

/**
 * Presence of remote changes: a short background flash on the rows others changed and a polite
 * screen reader announcement, at most one per 3 s (extra changes inside the window are merged into
 * one summary at its end).
 */

export const FLASH_MS = 600;
export const ANNOUNCE_GAP_MS = 3000;

/* ---------------------------------------------------------------- flash */

const flashing = new Map<string, number>();

const selectorFor = (id: string) => {
  const q = CSS.escape(id);
  return `[data-row-id="${q}"], [data-item-id="${q}"]`;
};

function startFlash(id: string): boolean {
  const els = document.querySelectorAll<HTMLElement>(selectorFor(id));
  if (els.length === 0) return false;
  for (const el of els) {
    // Restart the animation when the same row changes twice in a row.
    el.removeAttribute("data-live-flash");
    void el.offsetWidth;
    el.setAttribute("data-live-flash", "");
    window.setTimeout(() => el.removeAttribute("data-live-flash"), FLASH_MS);
  }
  return true;
}

/**
 * Flashes the row(s) of an item. A row that is not in the document yet (an item that was just
 * created, a list that re-renders) is looked for again a few times while it appears.
 */
export function flashItem(id: string) {
  const now = Date.now();
  if ((flashing.get(id) ?? 0) > now - FLASH_MS / 2) return;
  flashing.set(id, now);
  if (flashing.size > 200) for (const [k, t] of flashing) if (t < now - 5000) flashing.delete(k);
  // Two frames: the cache write has been committed to the DOM by then.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      if (startFlash(id)) return;
      let tries = 0;
      const retry = window.setInterval(() => {
        if (startFlash(id) || ++tries >= 8) window.clearInterval(retry);
      }, 80);
    }),
  );
}

/* ---------------------------------------------------------------- announcements */

export type Change = {
  kind: "created" | "updated" | "deleted" | "commented";
  key: string;
  actor: string;
};

function describe(c: Change): string {
  switch (c.kind) {
    case "created":
      return m.live_item_created_by({ key: c.key, name: c.actor });
    case "deleted":
      return m.live_item_deleted_by({ key: c.key, name: c.actor });
    case "commented":
      return m.live_comment_by({ key: c.key, name: c.actor });
    default:
      return m.live_item_updated_by({ key: c.key, name: c.actor });
  }
}

let text = "";
const listeners = new Set<() => void>();
let lastAt = 0;
let pending: Change[] = [];
let timer: number | undefined;

function say(message: string) {
  lastAt = Date.now();
  text = message;
  for (const l of [...listeners]) l();
}

function flush() {
  timer = undefined;
  const batch = pending;
  pending = [];
  if (batch.length === 0) return;
  say(batch.length === 1 ? describe(batch[0] as Change) : m.live_items_changed({ count: batch.length }));
}

/** Queues an announcement for the live region (see `useAnnouncement`). */
export function announceChange(change: Change) {
  pending.push(change);
  if (timer !== undefined) return;
  const wait = Math.max(0, lastAt + ANNOUNCE_GAP_MS - Date.now());
  timer = window.setTimeout(flush, wait);
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function useAnnouncement(): string {
  return useSyncExternalStore(
    subscribe,
    () => text,
    () => "",
  );
}

/** Test hook: forget pending output. */
export function resetPresence() {
  if (timer !== undefined) window.clearTimeout(timer);
  timer = undefined;
  pending = [];
  lastAt = 0;
  text = "";
  flashing.clear();
}
